import apis from '@api/api';
import noticeDispatcher from './noticeDispatcher';

/**
 * Integration connection validation utility
 * Validates connected integrations by testing their API connections
 */

interface Integration {
  _id?: string;
  id?: string;
  title: string;
  meta?: string;
  description?: string;
}

interface Authentications {
  [key: string]: any;
  huggingface?: {
    token?: string;
    username?: string;
  };
}

interface ValidationResult {
  integration: Integration;
  isValid: boolean;
  error?: string;
}

/**
 * Maps integration identifier (meta/title) to its corresponding profile API function
 */
const getProfileApiForIntegration = (integration: Integration): (() => Promise<any>) | null => {
  const meta = integration.meta?.toLowerCase().trim() || '';
  const title = integration.title?.toLowerCase().trim() || '';

  // Handle X/Twitter (can be 'x' or 'twitter')
  if (meta === 'x' || title === 'x' || meta === 'twitter' || title === 'twitter') {
    return apis.getXProfile;
  }

  // Handle other integrations by meta
  const metaMap: { [key: string]: () => Promise<any> } = {
    'facebook': apis.getFacebookProfile,
    'instagram': apis.getInstagramProfile,
    'linkedin': apis.getLinkedInProfile,
    'youtube': apis.getYoutubeProfile,
    'whatsapp': apis.getWhatsAppProfile,
    'stripe': apis.getStripeProfile,
    'slack': apis.getSlackProfile,
    'wordpress': apis.getWordpressProfile,
    'huggingface': apis.getHuggingFaceProfile,
  };

  if (metaMap[meta]) {
    return metaMap[meta];
  }

  // Fallback: try to match by title
  const titleMap: { [key: string]: () => Promise<any> } = {
    'facebook': apis.getFacebookProfile,
    'instagram': apis.getInstagramProfile,
    'linkedin': apis.getLinkedInProfile,
    'youtube': apis.getYoutubeProfile,
    'whatsapp': apis.getWhatsAppProfile,
    'stripe': apis.getStripeProfile,
    'slack': apis.getSlackProfile,
    'wordpress': apis.getWordpressProfile,
    'hugging face': apis.getHuggingFaceProfile,
    'huggingface': apis.getHuggingFaceProfile,
  };

  if (titleMap[title]) {
    return titleMap[title];
  }

  // No matching API function found
  return null;
};

/**
 * Checks if an integration is connected based on authentications object
 */
const isIntegrationConnected = (integration: Integration, authentications: Authentications): boolean => {
  if (!authentications || !integration) {
    return false;
  }

  // Special handling for Hugging Face (uses API token, stored as 'huggingface')
  const isHuggingFace = 
    integration.meta?.toLowerCase().replace('-', '') === 'huggingface' || 
    integration.title?.toLowerCase().replace(/\s+/g, '') === 'huggingface';

  if (isHuggingFace) {
    return !!(authentications?.huggingface?.token || authentications?.huggingface?.username);
  }

  // For other integrations, check if the title (lowercase) exists as a key in authentications
  return Object.keys(authentications).includes(integration.title.toLowerCase());
};

/**
 * Validates a single integration connection by calling its profile API
 */
export const validateIntegrationConnection = async (
  integration: Integration,
  authentications: Authentications
): Promise<ValidationResult> => {
  // Check if integration is connected
  if (!isIntegrationConnected(integration, authentications)) {
    return {
      integration,
      isValid: true, // Not connected, so no validation needed
    };
  }

  // Get the appropriate profile API function
  const profileApi = getProfileApiForIntegration(integration);
  
  if (!profileApi) {
    // No API function available for this integration, skip validation
    return {
      integration,
      isValid: true, // Skip validation if no API function
    };
  }

  try {
    // Call the profile API to test the connection
    await profileApi();
    
    return {
      integration,
      isValid: true,
    };
  } catch (error: any) {
    // Check if error indicates an invalid/expired connection
    const status = error?.response?.status || error?.status;
    const isAuthError = status === 401 || status === 403;
    const isNetworkError = !status && (error?.message?.includes('Network') || error?.code === 'NETWORK_ERROR');

    // Consider connection failed if:
    // - 401 (Unauthorized)
    // - 403 (Forbidden)
    // - Network error
    // - Error message indicates invalid/expired token
    const isConnectionFailed = 
      isAuthError || 
      isNetworkError ||
      error?.message?.toLowerCase().includes('token') ||
      error?.message?.toLowerCase().includes('expired') ||
      error?.message?.toLowerCase().includes('unauthorized') ||
      error?.message?.toLowerCase().includes('authenticate');

    return {
      integration,
      isValid: !isConnectionFailed,
      error: error?.message || 'Connection validation failed',
    };
  }
};

/**
 * Validates all connected integrations in parallel
 */
export const validateAllConnectedIntegrations = async (
  integrations: Integration[],
  authentications: Authentications
): Promise<ValidationResult[]> => {
  if (!integrations || !Array.isArray(integrations) || integrations.length === 0) {
    return [];
  }

  if (!authentications || Object.keys(authentications).length === 0) {
    return [];
  }

  // Filter to only connected integrations
  const connectedIntegrations = integrations.filter(integration => 
    isIntegrationConnected(integration, authentications)
  );

  if (connectedIntegrations.length === 0) {
    return [];
  }

  // Validate all connected integrations in parallel
  const validationPromises = connectedIntegrations.map(integration =>
    validateIntegrationConnection(integration, authentications)
  );

  const results = await Promise.allSettled(validationPromises);
  
  // Map settled results to ValidationResult array
  return results.map((result, index) => {
    if (result.status === 'fulfilled') {
      return result.value;
    } else {
      // If validation itself failed (not the API call), mark as valid to avoid false positives
      return {
        integration: connectedIntegrations[index],
        isValid: true,
        error: result.reason?.message || 'Validation error',
      };
    }
  });
};

/**
 * Validates integrations and dispatches notifications for failed connections
 */
export const validateAndNotifyIntegrationConnections = async (
  integrations: Integration[],
  authentications: Authentications
): Promise<void> => {
  try {
    const results = await validateAllConnectedIntegrations(integrations, authentications);
    
    // Filter to only failed validations
    const failedResults = results.filter(result => !result.isValid);

    // Dispatch notifications for each failed connection
    for (const result of failedResults) {
      await noticeDispatcher({
        title: `${result.integration.title} Connection Issue`,
        body: `Your ${result.integration.title} connection may need to be re-authenticated. Please check your integrations.`,
        timestamp: Date.now(),
        type: 'warning',
        screen: 'Integrations',
        stack: 'Dashboard',
        // Include integration name in the notification payload for search filtering
        integrationName: result.integration.title,
      });
    }
  } catch (error) {
    // Silently handle errors in validation to avoid disrupting the app
    console.error('Error validating integration connections:', error);
  }
};

